<template>
    <div class="flex h-screen w-screen overflow-hidden">
        <div ref="sidebarContainerRef" class="shrink-0">
            <SidebarMenu />
        </div>
        <div class="flex min-w-0 flex-1 flex-col">
            <AutomaticProcessingNotification />
            <div
                class="sidebar-content-offset flex flex-1 overflow-hidden transition-[padding-top] duration-150"
            >
                <slot />
            </div>
        </div>
        <ResetButton />
    </div>
</template>

<script setup lang="ts">
    import { useAutomaticProcessingPaused } from '@/api/admin';
    import AutomaticProcessingNotification from '@/components/common/AutomaticProcessingNotification.vue';
    import { useSignalRNotifications } from '@/composables/useSignalRNotification';
    import type { AutomaticProcessingStatusNotification } from '@/models/automaticProcessingStatus';
    import useSignalRStore from '@/stores/useSignalRStore';
    import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

    useAutomaticProcessingPaused();
    const signalRStore = useSignalRStore();

    useSignalRNotifications({
        notificationType: ['CreditNoteProcessAutomatedStatusNotification'],
        onNotificationReceived: notification => {
            signalRStore.updateAutomaticProcessingStatus(
                notification as AutomaticProcessingStatusNotification
            );
        },
    });

    const sidebarContainerRef = ref<HTMLElement | null>(null);
    let sidebarResizeObserver: ResizeObserver | null = null;

    const updateSidebarOffset = () => {
        const width = sidebarContainerRef.value?.getBoundingClientRect().width ?? 0;
        document.documentElement.style.setProperty('--layout-left-offset', `${Math.ceil(width)}px`);
    };

    onMounted(async () => {
        await nextTick();
        updateSidebarOffset();

        sidebarResizeObserver = new ResizeObserver(() => {
            updateSidebarOffset();
        });

        if (sidebarContainerRef.value) {
            sidebarResizeObserver.observe(sidebarContainerRef.value);
        }
    });

    onBeforeUnmount(() => {
        sidebarResizeObserver?.disconnect();
        sidebarResizeObserver = null;
        document.documentElement.style.setProperty('--layout-left-offset', '0px');
    });
</script>
